import React, { useContext, useEffect, useState } from "react";

import Form, {
  FormButton,
  FormCheckbox,
  FormGroup,
  FormInput,
  FormRadio,
} from "@/components/Form";
import {
  FormField,
  FormFields,
  FormFieldType,
  getAllFieldsInArray,
  getAvailableFormFields,
  isValid,
} from "@/components/Form/FormFields";
import FormSelect from "@/components/Form/Select";
import { getApiUrl, getHansIdSettings, getPrivacyLink } from "@/config";
import LocaleContext from "@/i18n";
import InteractionService from "@/service/Interaction";
import {
  CustomField,
  HansIdResponse,
  RegistrationFormInteractionOutro,
  RegistrationStatus,
} from "@/service/Model";
import PostMessageService from "@/service/PostMessage";
import { TealiumApiService, TealiumGender } from "@/service/Tealium";
import TrackingService from "@/service/Tracking";
import Svg from "@/styles/Svg";
import { isMobilePhone } from "@/utils/getters";
import { Enum } from "@/utils/transform";

import { OutroPropsExtented } from "../index";
import { OutroFeedback, OutroFeedbackIcon, RegistrationStep } from "../styles";

interface RegistrationProps
  extends RegistrationFormInteractionOutro,
    OutroPropsExtented {
  title?: string;
  brand?: string;
  tealiumConfig?: ITealium;
  registrationStatus?: RegistrationStatus;
  applyMaxHeight?: boolean;
  hansId?: string;
  hansIdExists?: boolean;
  isPreview?: boolean;
  onClose: () => void;
  onSubmit: (form: {
    [key: string]: string;
  }) => Promise<RegistrationStatus | undefined>;
  onSubmitEnd?: (status: RegistrationStatus) => void;
}

interface FieldData {
  value?: string;
  checked?: boolean;
  valid: boolean;
}

interface FormData {
  [fieldName: string]: FieldData;
}

const CUSTOM_FIELDS = "customFields";

const tealiumApi = new TealiumApiService();

const postMessageService = new PostMessageService(window.top || window.parent);

// [page][row][column]
const initFormPages = (
  formFields: FormFields,
  props: RegistrationProps
): FormField[][][] => {
  const {
    advertisementOptIn,
    hansIdOptIn,
    lightRegistration,
    customFields,
    hansId,
    hansIdExists,
  } = props;
  const pages: FormField[][][] = [];
  const showHansIdOptIn = hansIdOptIn && hansId && !hansIdExists;

  if (lightRegistration) {
    const personalInformation = [
      [formFields.outroTitle],
      [formFields.firstName, formFields.lastName],
      [formFields.email],
      [formFields.infoText],
    ];

    if (advertisementOptIn) {
      // insert at 2
      personalInformation.splice(2, 0, [formFields.advertisementOptIn]);
    }

    if (showHansIdOptIn) {
      // insert at 3
      personalInformation.splice(3, 0, [formFields.hansIdOptIn]);
    }

    pages.push(personalInformation);
  } else {
    const personalInformation = [
      [formFields.outroTitle],
      [formFields.gender],
      [formFields.firstName, formFields.lastName],
      [formFields.email],
    ];

    if (customFields) {
      customFields.forEach((customField: CustomField, index: number) => {
        // insert at 1
        personalInformation.splice(1, 0, [
          {
            name: `${CUSTOM_FIELDS}-${index}`,
            label: customField.label,
            type: customField.type,
            options: customField.options,
            validation: {
              required: true, // Custom field are always required (could be a setting).
            },
          },
        ]);
      });
    }

    const addressInformation = [
      [formFields.prizeText],
      [formFields.street, formFields.houseNumber, formFields.postalcode],
      [formFields.city, formFields.telephone],
      [formFields.infoText],
    ];

    if (advertisementOptIn) {
      // insert at 3
      addressInformation.splice(3, 0, [formFields.advertisementOptIn]);
    }

    if (showHansIdOptIn) {
      // insert at 4
      addressInformation.splice(4, 0, [formFields.hansIdOptIn]);
    }

    if (isMobilePhone()) {
      pages.push(personalInformation.concat(addressInformation));
    } else {
      pages.push(personalInformation);
      pages.push(addressInformation);
    }
  }

  return pages;
};

const Registration: React.FC<RegistrationProps> = (
  props: RegistrationProps
): JSX.Element => {
  const {
    registrationStatus,
    icons,
    brand,
    termsAndConditionsLink,
    privacyPolicyLink,
    isPreview = false,
    outroTitle,
  } = props;
  const api = new InteractionService(getApiUrl(isPreview));
  const localeContext = useContext(LocaleContext);
  const { translation } = localeContext;

  const [activePage, setActivePage] = useState<number>(0);
  const [hansId, setHansId] = useState<string | undefined>(props.hansId);
  const [hansIdExists, setHansIdExists] = useState<boolean | undefined>(
    props.hansIdExists
  );
  const [loading, setLoading] = useState<boolean>(!hansId);
  const [pages, setPages] = useState<FormField[][][]>(
    initFormPages(getAvailableFormFields(translation, brand), props)
  );
  const [formData, setFormData] = useState<FormData>({});
  const initHansId = () => {
    const hansIdSettings = getHansIdSettings();
    postMessageService.send(
      {
        message: hansIdSettings.postMethod,
      },
      (data: HansIdResponse) => {
        const hansIdAsString = String(data.hansId);
        if (hansIdAsString) {
          setHansId(hansIdAsString);
          api
            .hansIdExists(hansIdAsString)
            .then((exists: boolean) => {
              setHansIdExists(exists);
              setLoading(false);
            })
            .catch(() => {
              setHansIdExists(false);
              setLoading(false);
            });
        } else {
          setLoading(false);
        }
      }
    );

    // Timeout if adLib is not reachable
    setTimeout(() => {
      setLoading(false);
    }, hansIdSettings.timeout);
  };

  useEffect(() => {
    if (!hansId) {
      initHansId();
    }
    TrackingService.getInstance().trackOnOutroImpression();
  }, []);

  useEffect(() => {
    // reinit of the form is needed
    setPages(
      initFormPages(getAvailableFormFields(translation, brand), {
        ...props,
        hansId,
        hansIdExists,
      })
    );
  }, [hansId, hansIdExists]);

  const renderFormStatus = (status: RegistrationStatus): JSX.Element => {
    let icon = null;
    let feedback = translation.request.pending;

    if (status === RegistrationStatus.Error) {
      icon = icons.Fail;
      feedback = translation.request.error;
    } else if (status === RegistrationStatus.Success) {
      icon = icons.Success;
      feedback = translation.thankYou;
    }

    return (
      <OutroFeedback data-qa="registration.outro.feedback">
        {icon && (
          <OutroFeedbackIcon success={status === RegistrationStatus.Success}>
            <Svg contents={icon} height={65} width={65} />
          </OutroFeedbackIcon>
        )}
        <h3>{feedback}</h3>
        {formData.advertisementOptIn?.value &&
          status === RegistrationStatus.Success && (
            <h4>{translation.confirmRegistration}</h4>
          )}
      </OutroFeedback>
    );
  };

  const renderForm = (): JSX.Element => {
    const { applyMaxHeight } = props;
    const isLastStep = pages.length === activePage + 1;
    const page = pages[activePage];

    return (
      <RegistrationStep applyMaxHeight={!!applyMaxHeight}>
        {page.map((row: FormField[], rowIndex: number) => (
          <React.Fragment key={`row-${rowIndex}`}>
            {renderRowElement(row)}
          </React.Fragment>
        ))}
        {isLastStep && (
          <FormButton
            type={"submit"}
            disabled={false}
            label={translation.registration.submit}
            onClick={handleSubmit}
            dataQA="registration.submit"
          />
        )}
        {!isLastStep && (
          <FormButton
            type={"button"}
            disabled={false}
            label={translation.registration.next}
            onClick={handleNextPage}
          />
        )}
      </RegistrationStep>
    );
  };

  const renderRowElement = (row: FormField[]): JSX.Element => {
    return (
      <FormGroup>
        {row.map((field: FormField, columnIndex: number) => {
          const fieldData = formData[field.name] || { valid: true };
          return (
            <React.Fragment key={`${field.name}-${columnIndex}`}>
              {renderFieldElement(field, fieldData)}
            </React.Fragment>
          );
        })}
      </FormGroup>
    );
  };

  const renderFieldElement = (
    field: FormField,
    fieldData: FieldData
  ): JSX.Element | null => {
    switch (field.type) {
      case FormFieldType.Input:
        return (
          <FormInput
            name={field.name}
            label={field.label}
            required={{
              active: !!field.validation?.required, // unused
              text: translation.registration.required,
            }}
            value={fieldData.value || ""}
            onChange={(value: string) => handleChange(value, field)}
            valid={fieldData.valid}
          />
        );
      case FormFieldType.Select:
        return (
          <FormSelect
            name={field.name}
            label={field.label || ""}
            required={{
              active: !!field.validation?.required, // unused
              text: translation.registration.required,
            }}
            value={fieldData.value || ""}
            options={field.options || []}
            onChange={(value: string) => handleChange(value, field)}
            valid={fieldData.valid}
          />
        );
      case FormFieldType.Radio:
        return (
          <FormGroup fieldType={field.type}>
            {field.options?.map((fieldOption: string, optionIndex: number) => (
              <FormRadio
                key={`option-${optionIndex}`}
                name={field.name}
                label={translation.registration[fieldOption] || fieldOption}
                value={fieldOption}
                checked={fieldData.value === field.name}
                onChange={(value: string) => handleChange(value, field)}
              />
            ))}
          </FormGroup>
        );
      case FormFieldType.Checkbox:
        return (
          <FormCheckbox
            icons={icons}
            name={field.name}
            label={field.label || ""}
            onChange={(checked: boolean) => handleChange(checked, field)}
            checked={!!fieldData.checked}
          />
        );
      case FormFieldType.Static:
        return field.element
          ? field.element(
              termsAndConditionsLink,
              privacyPolicyLink || getPrivacyLink(brand),
              outroTitle
            )
          : null;
      default:
        return null;
    }
  };

  const handleChange = (value: string | boolean, field: FormField): void => {
    const clonedFormData = { ...formData };

    if (typeof value === "boolean") {
      clonedFormData[field.name] = {
        checked: value,
        valid: true, // Hint: required is not checked for checkboxes
      };
    } else {
      clonedFormData[field.name] = {
        value,
        valid: isValid(field, value),
      };
    }
    setFormData(clonedFormData);
  };

  const validatePages = (pageIndex?: number): boolean => {
    const clonedFormData = { ...formData };
    const formFields =
      pageIndex !== undefined
        ? getAllFieldsInArray(pages[pageIndex])
        : getAllFieldsInArray(pages);

    let valid = true;
    formFields.forEach((formField: FormField) => {
      let fieldData: FieldData = { valid: true };
      if (formField.type === FormFieldType.Checkbox) {
        fieldData.checked = clonedFormData[formField.name]?.checked || false;
      } else {
        const value = clonedFormData[formField.name]?.value || "";
        fieldData = {
          value,
          valid: isValid(formField, value),
        };
      }

      if (!fieldData.valid) {
        valid = false;
      }

      clonedFormData[formField.name] = fieldData;
    });

    // Update formData state will show errors after render
    setFormData(clonedFormData);
    return valid;
  };

  const handleNextPage = (): void => {
    if (!validatePages(activePage)) {
      return;
    }
    setActivePage(activePage + 1);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validatePages()) {
      return;
    }
    const { tealiumConfig, onSubmit, onSubmitEnd } = props;
    const genderCode = Enum.get(TealiumGender, String(formData.gender?.value));

    // Enrich tealium profile with gender
    if (tealiumConfig && genderCode) {
      const query: ITealium = {
        tealium_account: tealiumConfig.tealium_account,
        tealium_profile: tealiumConfig.tealium_profile,
        tealium_vid: tealiumConfig.tealium_vid,
        gender: genderCode,
        is_premium_user: tealiumConfig.is_premium_user,
      };

      tealiumApi.send(query);
    }

    const payload: any = {};
    Object.keys(formData).forEach((fieldName: string) => {
      let value: any = "";
      if (formData[fieldName].value !== undefined) {
        value = formData[fieldName].value;
      } else if (formData[fieldName].checked !== undefined) {
        value = formData[fieldName].checked;
      }
      if (fieldName.startsWith(CUSTOM_FIELDS)) {
        payload[CUSTOM_FIELDS] = payload[CUSTOM_FIELDS] || [];
        payload[CUSTOM_FIELDS].push(value);
      } else {
        payload[fieldName] = value;
      }
    });

    if (hansId) {
      payload.hansId = hansId;
    }

    // need key-value pair here as payload
    onSubmit(payload).then(
      (status: RegistrationStatus) => onSubmitEnd && onSubmitEnd(status)
    );
    TrackingService.getInstance().trackOnOutroClick();
  };

  if (loading) {
    return <></>;
  }

  return (
    <React.Fragment>
      {!registrationStatus || registrationStatus === RegistrationStatus.None
        ? renderForm()
        : renderFormStatus(registrationStatus)}
    </React.Fragment>
  );
};

export default Form(Registration);
